import { transition, trigger, useAnimation } from '@angular/animations';
import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';

import { fadeIn } from '../shared/animations/animations';
import { InstrumentationParam, InstrumentationParams, InstrumentationService } from '../shared/instrumentation/instrumentation.service';
import { MruFileItem } from '../shared/mru/mru-file-item.model';
import { DeviceDetector } from '../shared/services/device-detector.service';
import { DocumentService } from '../shared/services/document.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { LocService } from '../shared/shared-context/loc.service';
import { CarouselCard } from './Models/carousel-card.model';
import { Carousel } from './Models/carousel.model';

import './carousel.component.css';

@Component({
    selector: 'ohp-carousel',
    templateUrl: './carousel.component.html',
    animations: [
        trigger('fadeIn', [
            transition(':enter', useAnimation(fadeIn, { params: { fadeInDuration: '500ms', opacity: 1 }}))
        ])
    ]
})

export class CarouselComponent implements OnInit {
    @ViewChild('carouselElement') public carouselElement: ElementRef;
    @ViewChild('carouselContainer') public carouselContainer: ElementRef;

    @Input() public carousel: Carousel;
    @Input() public navClickLogging: (params: InstrumentationParams, elementId: string) => void;
    @Input() public fourCardsMax: boolean;
    @Input() public isSuggestedActions: boolean;

    public canSwipe: boolean;
    public cards: Array<CarouselCard | MruFileItem>;
    public cardIncrement: number = 4;
    public currentCarouselPage: number = 0;
    public disableAnimation: boolean;
    public maxCardsPerLine: number = 4;
    public navigationAnnouncement: string;
    public nextPage: string;
    public previousPage: string;
    public cardMinWidth: number = 217;
    public cardMargin: number = 12;

    protected maxCardContainerWidths: Array<number>;

    constructor(
        protected readonly capabilitiesService: CapabilitiesService,
        protected readonly changeDetector: ChangeDetectorRef,
        protected readonly deviceDetector: DeviceDetector,
        protected readonly documentService: DocumentService,
        protected readonly instrumentationService: InstrumentationService,
        protected readonly locService: LocService
    ) {
    }

    public ngOnInit() {
        this.sharedInit();

        this.cards = this.carousel.cards;
        this.canSwipe = !this.deviceDetector.isDesktopDevice();

        if (this.carousel.cards.length > 0) {
            this.maxCardContainerWidths = [
                this.maxCardContainerWidth(1),
                this.maxCardContainerWidth(2),
                this.maxCardContainerWidth(3),
                this.maxCardContainerWidth(4)
            ];
        }
    }

    public navigateCards(direction: number): boolean {
        const nextIndex = this.currentCarouselPage + direction * this.cardIncrement;
        if (nextIndex < this.cards.length && nextIndex >= 0) {
            this.navigationAnnouncement = '';
            if (direction < 0) {
                setTimeout(() => this.navigationAnnouncement = this.previousPage, 0);
            } else {
                setTimeout(() => this.navigationAnnouncement = this.nextPage, 0);
            }

            this.currentCarouselPage = nextIndex;
            this.logNavClick(direction);
            return true;
        }
        return false;
    }

    public updateCarouselCardData(isRecommendedMotion: boolean = false, measureWindow = false): void {
        if (this.carouselElement) {
            this.cardIncrement = this.getNumCards(isRecommendedMotion, measureWindow);
        }
    }

    public trackByIndex(index: number): number {
        return index;
    }

    protected sharedInit() {
        this.cardMinWidth = this.isSuggestedActions ? 368 : 217;
        this.cardMargin = this.isSuggestedActions ? 20 : 12;
        this.disableAnimation = this.capabilitiesService.capabilities.disableAnimation;
        this.nextPage = this.locService.ButtonNextPage;
        this.previousPage = this.locService.ButtonPreviousPage;
    }

    protected maxCardContainerWidth(numCardsOnLine: number): number {
        return (this.cardMinWidth + this.cardMargin) * numCardsOnLine;
    }

    protected logNavClick(direction: number): void {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.Index] = this.currentCarouselPage.toString();
        params[InstrumentationParam.Increment] = this.cardIncrement.toString();
        params[InstrumentationParam.TotalSize] = this.cards.length.toString();
        const elementId = direction < 0 ? 'NavPrevious' : 'NavNext';

        this.navClickLogging(params, elementId);
    }

    protected getNumCards(isRecommendedMotion: boolean = false, measureWindow = false): number {
        if (!this.cards || !this.cards.length) {
            return 0;
        }
        this.changeDetector.detectChanges();
        let containerWidth = measureWindow ? this.documentService.nativeDocument.documentElement.clientWidth : (this.carouselContainer.nativeElement.clientWidth + this.cardMargin);
        if (isRecommendedMotion && !this.isSuggestedActions && !measureWindow) {
            containerWidth -= 50;
        }
        for (let numCards = this.maxCardsPerLine; numCards > 0; numCards--) {
            if (containerWidth >= this.maxCardContainerWidths[numCards - 1]) {
                return numCards;
            }
        }
    }
}
